import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  Users,
  FileText,
  LogOut,
  ArrowLeft,
  MessageSquare,
  DollarSign,
  History,
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";

// Import refactored components
import {
  TimeEntriesCard,
  InvoicesCard,
  ClientFilesCard,
  AddTimeDialog,
  CreateInvoiceDialog,
  ClientTasksCard,
  CreateTaskDialog,
} from "../../components/admin";

const AdminClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user, token, API } = useAuth();
  const [clientData, setClientData] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [clientFiles, setClientFiles] = useState([]);
  const [clientTasks, setClientTasks] = useState([]);
  const [rateHistory, setRateHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    fetchRateHistory();
    fetchTasks();
  }, [clientId]);

  const fetchRateHistory = async () => {
    try {
      const response = await fetch(`${API}/admin/clients/${clientId}/rate-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRateHistory(data);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await fetch(`${API}/tasks/admin?client_id=${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setClientTasks(data);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const handleCreateTask = async (taskData) => {
    try {
      const params = new URLSearchParams({
        client_id: clientId,
        title: taskData.title,
        description: taskData.description,
        task_type: taskData.task_type,
        priority: taskData.priority,
        ...(taskData.due_date && { due_date: taskData.due_date }),
      });
      
      const response = await fetch(`${API}/tasks/?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success("Task created and notification sent to client");
        fetchTasks();
        return true;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create task");
        return false;
      }
    } catch (error) {
      toast.error("Failed to create task");
      return false;
    }
  };

  const fetchData = async () => {
    try {
      const [clientRes, consultantsRes, filesRes] = await Promise.all([
        fetch(`${API}/admin/clients/${clientId}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/consultants`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/clients/${clientId}/files`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (clientRes.ok) setClientData(await clientRes.json());
      if (consultantsRes.ok) setConsultants(await consultantsRes.json());
      if (filesRes.ok) setClientFiles(await filesRes.json());
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignConsultant = async (consultantId) => {
    try {
      const params = new URLSearchParams({ assigned_consultant_id: consultantId });
      const response = await fetch(`${API}/admin/clients/${clientId}?${params.toString()}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Consultant assigned");
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to assign consultant");
    }
  };

  const handleUpdateHours = async (hours) => {
    try {
      const params = new URLSearchParams({ monthly_hours: hours });
      const response = await fetch(`${API}/admin/clients/${clientId}?${params.toString()}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Hours updated");
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to update hours");
    }
  };

  const handleUpdateHourlyRate = async (rate) => {
    try {
      const params = new URLSearchParams({ hourly_rate: rate });
      const response = await fetch(`${API}/admin/clients/${clientId}?${params.toString()}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Hourly rate updated");
        fetchData();
        fetchRateHistory(); // Refresh rate history
      }
    } catch (error) {
      toast.error("Failed to update hourly rate");
    }
  };

  const handleAddTimeEntry = async (timeEntry) => {
    try {
      const params = new URLSearchParams({
        client_id: clientId,
        hours: timeEntry.hours,
        description: timeEntry.description,
        ...(timeEntry.date && { date: timeEntry.date }),
        ...(timeEntry.category && { category: timeEntry.category }),
      });
      const response = await fetch(`${API}/admin/time-entries?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Time entry added");
        fetchData();
        return true;
      }
    } catch (error) {
      toast.error("Failed to add time entry");
    }
    return false;
  };

  const handleCreateInvoice = async (invoice) => {
    try {
      const params = new URLSearchParams({
        client_id: clientId,
        amount: invoice.amount,
        description: invoice.description,
        due_date: invoice.due_date,
      });
      
      // Add optional fields if present
      if (invoice.consultant_id) params.append("consultant_id", invoice.consultant_id);
      if (invoice.hours_worked) params.append("hours_worked", invoice.hours_worked);
      if (invoice.period_start) params.append("period_start", invoice.period_start);
      if (invoice.period_end) params.append("period_end", invoice.period_end);
      
      const response = await fetch(`${API}/admin/invoices?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Invoice created");
        fetchData();
        return true;
      }
    } catch (error) {
      toast.error("Failed to create invoice");
    }
    return false;
  };

  const handleDownloadInvoicePdf = async (invoiceId) => {
    try {
      const response = await fetch(`${API}/admin/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to download PDF");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice_${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Invoice PDF downloaded");
    } catch (error) {
      toast.error("Failed to download invoice PDF");
    }
  };

  const handleSendInvoiceEmail = async (invoiceId) => {
    try {
      const response = await fetch(`${API}/admin/invoices/${invoiceId}/send-email`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Invoice sent to ${data.email}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send email");
      }
    } catch (error) {
      toast.error("Failed to send invoice email");
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      const response = await fetch(`${API}/admin/clients/${clientId}/files/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("File downloaded successfully");
    } catch (error) {
      toast.error("Failed to download file");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-rnb-blue/20 border-t-rnb-blue rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-[1200px] mx-auto animate-fade-in">
            {/* Back Button */}
            <Button variant="ghost" className="mb-4 text-gray-500 hover:text-rnb-navy gap-1.5 -ml-2" size="sm" onClick={() => navigate("/admin/clients")}>
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to Clients
            </Button>

            {/* Client Header */}
            <div className="flex items-start justify-between mb-8">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-rnb-blue/10 rounded-2xl flex items-center justify-center">
                  <span className="font-heading text-2xl font-bold text-rnb-blue">
                    {clientData?.user?.name?.split(" ").map((n) => n[0]).join("") || "?"}
                  </span>
                </div>
                <div>
                  <h1 className="font-heading text-3xl font-bold text-rnb-navy">
                    {clientData?.user?.name}
                  </h1>
                  <p className="text-rnb-gray">
                    {clientData?.client?.company_name || clientData?.user?.email}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Link to={`/portal/chat/${clientData?.user?.user_id}`}>
                  <Button variant="outline">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Chat
                  </Button>
                </Link>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid md:grid-cols-5 gap-4 mb-8">
              {/* Assigned Consultant */}
              <Card>
                <CardContent className="p-4">
                  <Label className="text-xs text-rnb-gray">Assigned Consultant</Label>
                  <Select
                    value={clientData?.client?.assigned_consultant_id || ""}
                    onValueChange={handleAssignConsultant}
                  >
                    <SelectTrigger className="mt-2" data-testid="assign-consultant">
                      <SelectValue placeholder="Select consultant" />
                    </SelectTrigger>
                    <SelectContent>
                      {consultants.map((c) => (
                        <SelectItem key={c.user_id} value={c.user_id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>

              {/* Monthly Hours */}
              <Card>
                <CardContent className="p-4">
                  <Label className="text-xs text-rnb-gray">Monthly Hours</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      type="number"
                      defaultValue={clientData?.client?.monthly_hours || 0}
                      onBlur={(e) => handleUpdateHours(e.target.value)}
                      data-testid="monthly-hours"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Hourly Rate */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-rnb-gray">Hourly Rate</Label>
                    {rateHistory.length > 0 && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 px-2">
                            <History className="h-3 w-3 mr-1" />
                            <span className="text-xs">History</span>
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                              <DollarSign className="h-5 w-5 text-rnb-blue" />
                              Rate Change History
                            </DialogTitle>
                          </DialogHeader>
                          <div className="max-h-80 overflow-y-auto space-y-3 pt-4">
                            {rateHistory.map((change, index) => (
                              <div key={change.change_id || index} className="p-3 bg-slate-50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-red-500 line-through">${change.old_rate}</span>
                                    <span className="text-rnb-gray">→</span>
                                    <span className="font-mono text-green-600 font-semibold">${change.new_rate}</span>
                                  </div>
                                  <Badge variant="outline" className="text-xs">
                                    {change.new_rate > change.old_rate ? "+" : ""}
                                    {((change.new_rate - change.old_rate) / change.old_rate * 100).toFixed(1)}%
                                  </Badge>
                                </div>
                                <div className="text-xs text-rnb-gray">
                                  Changed by <span className="font-medium">{change.changed_by_name}</span>
                                  <span className="mx-2">•</span>
                                  {new Date(change.changed_at).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                    hour: "numeric",
                                    minute: "2-digit"
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    <span className="text-rnb-gray">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      defaultValue={clientData?.client?.hourly_rate || 150}
                      onBlur={(e) => handleUpdateHourlyRate(e.target.value)}
                      data-testid="hourly-rate"
                    />
                  </div>
                  <p className="text-xs text-rnb-gray mt-1">Used for invoicing</p>
                </CardContent>
              </Card>

              {/* Add Time Entry Dialog */}
              <AddTimeDialog onAddTimeEntry={handleAddTimeEntry} />

              {/* Create Invoice Dialog */}
              <CreateInvoiceDialog 
                onCreateInvoice={handleCreateInvoice} 
                consultants={consultants}
                clientData={clientData?.client}
              />

              {/* Create Task Dialog */}
              <CreateTaskDialog onCreateTask={handleCreateTask} />
            </div>

            {/* Tasks Section */}
            <div className="mb-8">
              <ClientTasksCard
                tasks={clientTasks}
                formatDate={formatDate}
              />
            </div>

            {/* Data Tables */}
            <div className="grid lg:grid-cols-2 gap-8">
              <TimeEntriesCard
                timeEntries={clientData?.time_entries}
                formatDate={formatDate}
              />
              <InvoicesCard
                invoices={clientData?.invoices}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                onDownloadPdf={handleDownloadInvoicePdf}
                onSendEmail={handleSendInvoiceEmail}
              />
            </div>

            <ClientFilesCard
              files={clientFiles}
              formatDate={formatDate}
              onDownloadFile={handleDownloadFile}
            />
      </div>
    </AdminLayout>
  );
};

export default AdminClientDetail;
